import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GroupInfo } from '~/Services/GroupServices'

export interface GroupFilter {
  queryString?: string
  columnFilters?: Record<string, any>
}

export interface GroupSort {
  field: string
  order: 'ascend' | 'descend'
}

export interface GroupReducerStore {
  data: GroupInfo[]
  allGroups: GroupInfo[]
  totalCount: number
  currentEntity: GroupInfo | null
  loading: boolean
  error: string | null
  filter: GroupFilter
  keyword: string
  columnFilters: Record<string, any>
  page: number
  pageSize: number
  expand: string | null
  sort?: GroupSort
}

const initialState: GroupReducerStore = {
  data: [],
  allGroups: [],
  totalCount: 0,
  currentEntity: null,
  loading: false,
  error: null,
  filter: {},
  keyword: '',
  columnFilters: {},
  page: 1,
  pageSize: 10,
  expand: null,
  sort: undefined,
}

export const GroupSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    // Get group list
    getGroupList: (
      state,
      action: PayloadAction<{
        orgId: string
        page?: number
        pageSize?: number
        filter?: GroupFilter
        sort?: GroupSort
        expand?: string
      }>,
    ) => {
      state.loading = true
      state.error = null
      if (action.payload.page) state.page = action.payload.page
      if (action.payload.pageSize) state.pageSize = action.payload.pageSize
      if (action.payload.filter) state.filter = action.payload.filter
      if (action.payload.sort) state.sort = action.payload.sort
      if (action.payload.expand) state.expand = action.payload.expand
    },
    getGroupListSuccess: (
      state,
      action: PayloadAction<{ results: GroupInfo[]; queryCount: number }>,
    ) => {
      state.data = action.payload.results
      state.totalCount = action.payload.queryCount
      state.loading = false
      state.error = null
    },
    getGroupListFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Get all group list
    getAllGroupList: state => {
      state.loading = true
      state.error = null
    },
    getAllGroupListSuccess: (
      state,
      action: PayloadAction<{ results: GroupInfo[]; queryCount: number }>,
    ) => {
      state.allGroups = action.payload.results
    },
    getAllGroupListFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Get group list by org id
    getGroupListByOrgId: (
      state,
      action: PayloadAction<{
        orgId: string
        name?: string
        status?: number
        onSuccess?: (groups: GroupInfo[]) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    getGroupListByOrgIdSuccess: (
      state,
      action: PayloadAction<{ results: GroupInfo[]; queryCount: number }>,
    ) => {
      state.data = action.payload.results
      state.totalCount = action.payload.queryCount
      state.loading = false
      state.error = null
    },
    getGroupListByOrgIdFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    setKeyword: (state, action: PayloadAction<string>) => {
      state.keyword = action.payload
      state.page = 1
    },

    setColumnFilters: (state, action: PayloadAction<Record<string, any>>) => {
      state.columnFilters = action.payload
      state.page = 1
    },

    setSort: (state, action: PayloadAction<GroupSort | undefined>) => {
      state.sort = action.payload
    },

    // Clear error
    clearError: state => {
      state.error = null
    },

    // Reset filter
    resetFilter: state => {
      state.filter = {}
      state.keyword = ''
      state.columnFilters = {}
      state.sort = undefined
      state.page = 1
    },

    // Create new group
    createGroup: (
      state,
      action: PayloadAction<{
        data: GroupInfo
        onSuccess?: (group: GroupInfo) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    createGroupSuccess: (state, action: PayloadAction<GroupInfo>) => {
      state.loading = false
      state.error = null
    },
    createGroupFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Update group
    updateGroup: (
      state,
      action: PayloadAction<{
        groupId: string
        data: any
        onSuccess?: (group: GroupInfo) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    updateGroupSuccess: (state, action: PayloadAction<GroupInfo>) => {
      state.loading = false
      state.error = null
      const index = state.data.findIndex(
        group => group.id === action.payload.id,
      )
      if (index !== -1) {
        state.data[index] = action.payload
      }
    },
    updateGroupFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Delete group
    deleteGroup: (
      state,
      action: PayloadAction<{
        orgId: string
        groupIds: string[]
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    deleteGroupSuccess: (state, action: PayloadAction<string[]>) => {
      state.loading = false
      state.error = null
      state.data = state.data.filter(
        group => group.id && !action.payload.includes(group.id),
      )
      state.totalCount = Math.max(0, state.totalCount - action.payload.length)
    },
    deleteGroupFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Delete group by id
    deleteGroupById: (
      state,
      action: PayloadAction<{
        id: string
        orgId: string
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    deleteGroupByIdSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = null
      state.data = state.data.filter(group => group.id !== action.payload)
      state.totalCount = Math.max(0, state.totalCount - 1)
    },
    deleteGroupByIdFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export all groups
    exportAllGroups: (
      state,
      action: PayloadAction<{
        orgId: string
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportAllGroupsSuccess: state => {
      state.loading = false
      state.error = null
    },
    exportAllGroupsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export groups in current page
    exportGroupsInCurrentPage: (
      state,
      action: PayloadAction<{
        orgId: string
        page?: number
        pageSize?: number
        filter?: GroupFilter
        sort?: GroupSort
        expand?: string
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportGroupsInCurrentPageSuccess: state => {
      state.loading = false
      state.error = null
    },
    exportGroupsInCurrentPageFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Export selected groups
    exportSelectedGroups: (
      state,
      action: PayloadAction<{
        orgId: string
        groupIds: string[]
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    exportSelectedGroupsSuccess: state => {
      state.loading = false
      state.error = null
    },
    exportSelectedGroupsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Assign group to staff
    assignGroupToStaff: (
      state,
      action: PayloadAction<{
        jobId: string
        organizationId: string
        userId: string
        onSuccess?: () => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    assignGroupToStaffSuccess: state => {
      state.loading = false
      state.error = null
    },
    assignGroupToStaffFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Import groups
    importGroups: (
      state,
      action: PayloadAction<{
        orgId: string
        file: File
        onSuccess?: (data: any) => void
        onFailure?: (error: string) => void
      }>,
    ) => {
      state.loading = true
      state.error = null
    },
    importGroupsSuccess: state => {
      state.loading = false
      state.error = null
    },
    importGroupsFail: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
  },
})

export const GroupActions = GroupSlice.actions
export const GroupReducer = GroupSlice.reducer
